import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BLOCKED_CACHE_KEY = 'chat_blocked_cache';

/**
 * Hook to monitor system status and check if chat input should be blocked
 * Implements caching that persists blocking state until status is resolved
 */
export const useSystemStatus = () => {
  const [systemStatus, setSystemStatus] = useState({
    isActive: false,
    statusType: null,
    severity: null,
    title: null,
    message: null,
    blockChatInput: false,
    estimatedDuration: null,
    affectedServices: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isChatBlocked, setIsChatBlocked] = useState(false);

  // Load cached blocking state on mount
  const loadCachedBlockingState = useCallback(() => {
    try {
      const cachedBlocked = localStorage.getItem(BLOCKED_CACHE_KEY);
      if (cachedBlocked) {
        const { isBlocked, timestamp, statusId } = JSON.parse(cachedBlocked);
        console.log('Loaded cached blocking state:', { isBlocked, timestamp, statusId });
        return { isBlocked, timestamp, statusId };
      }
    } catch (error) {
      console.error('Error loading cached blocking state:', error);
    }
    return { isBlocked: false, timestamp: null, statusId: null };
  }, []);

  // Save blocking state to cache
  const saveCachedBlockingState = useCallback((isBlocked, statusId = null) => {
    try {
      const cacheData = {
        isBlocked,
        timestamp: Date.now(),
        statusId
      };
      localStorage.setItem(BLOCKED_CACHE_KEY, JSON.stringify(cacheData));
      console.log('Saved blocking state to cache:', cacheData);
    } catch (error) {
      console.error('Error saving cached blocking state:', error);
    }
  }, []);

  // Clear blocking cache
  const clearBlockingCache = useCallback(() => {
    try {
      localStorage.removeItem(BLOCKED_CACHE_KEY);
      console.log('Cleared blocking cache');
    } catch (error) {
      console.error('Error clearing blocking cache:', error);
    }
  }, []);

  // Parse system status from response headers
  const parseStatusFromHeaders = (headers) => {
    const status = headers['x-system-status'];
    const blockChat = headers['x-system-status-block-chat'];
    
    // Only process if system status headers are present
    if (!status) {
      return null; // No system status headers found - don't change current state
    }
    
    if (status === 'operational') {
      return {
        isActive: false,
        statusType: null,
        severity: null,
        title: null,
        message: null,
        blockChatInput: false,
        estimatedDuration: null,
        affectedServices: [],
        statusId: null
      };
    }
    
    if (status === 'active') {
      const affectedServicesHeader = headers['x-system-status-affected-services'];
      const affectedServices = affectedServicesHeader ? affectedServicesHeader.split(',').map(s => s.trim()) : [];
      
      return {
        isActive: true,
        statusType: headers['x-system-status-type'],
        severity: headers['x-system-status-severity'],
        title: headers['x-system-status-title'],
        message: headers['x-system-status-message'],
        blockChatInput: blockChat === 'true',
        estimatedDuration: headers['x-system-status-duration'] || null,
        affectedServices: affectedServices,
        statusId: headers['x-system-status-id'] || Date.now().toString() // Fallback ID
      };
    }
    
    // Unknown status - don't change current state
    return null;
  };

  // Update blocking state based on current status and cache
  const updateBlockingState = useCallback((currentStatus) => {
    const cachedState = loadCachedBlockingState();
    
    // If status is operational (resolved), clear cache and unblock
    if (!currentStatus.isActive) {
      console.log('Status is operational - clearing cache and unblocking');
      clearBlockingCache();
      setIsChatBlocked(false);
      return;
    }
    
    // If status is active and has blockChatInput enabled
    if (currentStatus.isActive && currentStatus.blockChatInput) {
      console.log('Status is active with chat blocking enabled');
      
      // Check if this is a new status (different ID) or first time blocking
      const isNewStatus = !cachedState.statusId || cachedState.statusId !== currentStatus.statusId;
      
      if (isNewStatus || !cachedState.isBlocked) {
        console.log('New blocking status detected - caching and blocking');
        saveCachedBlockingState(true, currentStatus.statusId);
        setIsChatBlocked(true);
      } else {
        console.log('Using cached blocking state');
        setIsChatBlocked(cachedState.isBlocked);
      }
      return;
    }
    
    // If status is active but doesn't have blockChatInput, check cache
    if (currentStatus.isActive && !currentStatus.blockChatInput) {
      // If we have a cached blocking state, maintain it until status is resolved
      if (cachedState.isBlocked) {
        console.log('Status active but not blocking - maintaining cached blocked state');
        setIsChatBlocked(true);
      } else {
        console.log('Status active, not blocking, no cache - unblocked');
        setIsChatBlocked(false);
      }
      return;
    }
    
    // Default case - not blocked
    setIsChatBlocked(false);
  }, [loadCachedBlockingState, saveCachedBlockingState, clearBlockingCache]);

  // Fetch system status directly from API
  const fetchSystemStatus = useCallback(async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://192.168.86.39:8081/api/status/current'
        : `${process.env.REACT_APP_API_URL}/status/current`;
      
      const response = await axios.get(apiUrl);
      
      // Parse from headers if available, otherwise use response data
      const statusFromHeaders = parseStatusFromHeaders(response.headers);
      const statusFromData = response.data?.status;
      
      // Prefer response data if available, fallback to headers
      const finalStatus = statusFromData ? {
        isActive: statusFromData.isActive || false,
        statusType: statusFromData.statusType || null,
        severity: statusFromData.severity || null,
        title: statusFromData.title || null,
        message: statusFromData.message || null,
        blockChatInput: statusFromData.blockChatInput || false,
        estimatedDuration: statusFromData.estimatedDuration || null,
        affectedServices: statusFromData.affectedServices || [],
        statusId: statusFromData._id || statusFromData.id || Date.now().toString()
      } : statusFromHeaders;
      
      setSystemStatus(finalStatus);
      updateBlockingState(finalStatus);
    } catch (error) {
      console.error('Error fetching system status:', error);
      // On error, assume operational and clear any blocking
      const operationalStatus = {
        isActive: false,
        statusType: null,
        severity: null,
        title: null,
        message: null,
        blockChatInput: false,
        estimatedDuration: null,
        affectedServices: [],
        statusId: null
      };
      setSystemStatus(operationalStatus);
      updateBlockingState(operationalStatus);
    } finally {
      setIsLoading(false);
    }
  }, [updateBlockingState]);

  // Set up axios interceptor to read status from any response headers
  useEffect(() => {

    
    // Load cached blocking state on mount
    const cachedState = loadCachedBlockingState();
    if (cachedState.isBlocked) {
      console.log('Restoring cached blocking state on mount');
      setIsChatBlocked(true);
    }

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // Update status from headers on any successful response
        const statusFromHeaders = parseStatusFromHeaders(response.headers);
        if (statusFromHeaders !== null) {
          setSystemStatus(prevStatus => {
            // Only update if there's a meaningful change
            if (JSON.stringify(prevStatus) !== JSON.stringify(statusFromHeaders)) {
              updateBlockingState(statusFromHeaders);
              return statusFromHeaders;
            }
            return prevStatus;
          });
        }
        return response;
      },
      (error) => {
        // Also check headers on error responses
        if (error.response?.headers) {
          const statusFromHeaders = parseStatusFromHeaders(error.response.headers);
          if (statusFromHeaders !== null) {
            setSystemStatus(prevStatus => {
              if (JSON.stringify(prevStatus) !== JSON.stringify(statusFromHeaders)) {
                updateBlockingState(statusFromHeaders);
                return statusFromHeaders;
              }
              return prevStatus;
            });
          }
        }
        return Promise.reject(error);
      }
    );

    // Initial fetch
    fetchSystemStatus();

    // Set up polling interval for background status updates
    const pollInterval = setInterval(() => {
      console.log('Polling system status...');
      fetchSystemStatus();
    }, 30000); // Poll every 30 seconds

    // Cleanup interceptor and interval on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
      clearInterval(pollInterval);
    };
  }, [loadCachedBlockingState, updateBlockingState, fetchSystemStatus]);

  // Refresh status manually
  const refreshStatus = () => {
    setIsLoading(true);
    fetchSystemStatus();
  };



  // Check if user should bypass status (admin permissions)
  const shouldBypassStatus = useCallback((user) => {
    // Allow admins to bypass status screens
    return user?.role === 'admin' || user?.isAdmin || false;
  }, []);

  // Determine if system is down (should show status screen)
  const isSystemDown = systemStatus.isActive && (
    systemStatus.statusType === 'maintenance' || 
    systemStatus.severity === 'critical' ||
    systemStatus.blockChatInput ||
    (systemStatus.affectedServices && systemStatus.affectedServices.includes('all'))
  );

  return {
    systemStatus,
    isLoading,
    refreshStatus,
    clearBlockingCache, // Expose for manual cache clearing if needed
    // Convenience getters
    isChatBlocked, // Uses cached state that persists until resolved
    hasActiveStatus: systemStatus.isActive,
    statusMessage: systemStatus.message,
    statusTitle: systemStatus.title,
    // Additional status info for UI display
    blockingReason: isChatBlocked ? (systemStatus.message || 'Chat functionality is temporarily disabled') : null,
    // Missing properties needed by App.js
    isSystemDown,
    shouldBypassStatus
  };
};

export default useSystemStatus;